import { createTelemetryObject, createTelemetry } from '@shared/core/analytics';
import { PreviewOption, PaperProviderEnum, WebsiteDesingerTabs } from './WebsiteDesigner.types';
import { BrannanNavigation, Category, DesignLayoutType } from '@graphql/generated';
import { PageFunctionType } from '@shared/core/analytics/telemetry/types';
import { ApolloError } from '@apollo/client';
import { DesignConsoleRouteName, DesignConsoleTelemetryRoutes } from './components/DesignConsole/DesignConsoleRouter/DesignConsoleRouter.types';
import { ThemeVariant } from './routes/Template/Template.types';
import { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';

const category = 'admin.websiteDesignerV2';

// Create strongtly typed page events - DesignRoute type is used in multiple places re: routing
const pageTracking: { [tab in DesignConsoleTelemetryRoutes]: PageFunctionType } = {
  templates: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'templates',
  }),
  layout: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'layout',
  }),
  color: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'colors',
  }),
  details: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'details',
  }),
  font: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'fonts',
  }),
  art: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'art',
  }),
  css: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'css',
  }),
  eventPhoto: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'eventPhoto',
  }),
  fullScreenPreview: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'fullScreenPreview',
  }),
  websiteDesignerPrintCrossSellDialog: () => ({
    category: 'websiteDesignerV2',
    pagePrefix: 'admin',
    page: 'websiteDesignerPrintCrossSellDialog',
  }),
};

export const websiteDesignerTelemetry = createTelemetryObject({
  actions: {
    openPage: ({ label, section, customCategory }: { label: string; section: string; customCategory?: string }) => ({
      action: 'OpenPage',
      category: customCategory || category,
      actionType: 'click',
      label,
      extraInfo: {
        section,
      },
    }),
    openMeganav: ({ customCategory, isOpen }: { isOpen: boolean; customCategory?: string }) => ({
      action: 'ButtonInteracted',
      category: customCategory || category,
      label: isOpen ? 'MeganavOpened' : 'MeganavClosed',
    }),
    saveCssAndOpenClick: () => ({
      category,
      action: 'SaveCssAndOpen',
      actionType: 'click',
    }),
    publishClick: (
      template?: { id: string; isFavorite: boolean; isJoyPrintable: boolean },
      art?: { id: string; isFavorite: boolean },
      font?: { id: string; isFavorite: boolean },
      photoPresentationLayout?: PhotoPresentationLayout,
      brannanNavigation?: BrannanNavigation,
    ) => ({
      category,
      action: 'Publish',
      actionType: 'click',
      extraInfo: {
        template,
        art,
        font,
        photoPresentationLayout,
        brannanNavigation,
      },
    }),
    publishSucess: () => ({
      category,
      action: 'PublishSuccess',
      actionType: 'click',
    }),
    publishError: (e: Error) => ({
      category,
      action: 'Publish',
      actionType: 'error',
      extraInfo: { error: e.message },
    }),
    saveError: (mutation: string, error: ApolloError, extra = {}) => ({
      category,
      action: 'Save',
      actionType: 'error',
      extraInfo: { error, mutation, ...extra },
    }),
    selectionClick: (label: DesignConsoleTelemetryRoutes) => ({
      category,
      action: 'Section',
      actionType: 'click',
      label,
    }),
    previewClick: (label: PreviewOption) => ({
      category,
      action: 'Preview',
      actionType: 'click',
      label,
    }),
    selectTemplate: (label: DesignLayoutType) => ({
      category,
      action: 'SelectTemplate',
      actionType: 'click',
      label,
    }),
    layoutOptionClick: () => ({
      category,
      action: 'LayoutOption',
      actionType: 'click',
    }),
    themeThumbnailClick: (styleId: string, isJoyPrintable: boolean) => ({
      category,
      action: 'TriedStyle',
      actionType: 'click',
      label: styleId,
      extraInfo: {
        isJoyPrintable,
      },
    }),
    themeThumbnailArrowClick: () => ({
      category,
      action: 'ThemeOption',
      actionType: 'click',
    }),
    themeFilterCheckboxClick: (label: string) => ({
      category,
      action: 'ThemeFilterByCategory',
      actionType: 'click',
      label,
    }),
    themeNotAvailable: (styleId: string) => ({
      category,
      action: 'SeeThemeIsNotAvailable',
      actionType: 'click',
      label: styleId,
    }),
    switchLayoutButtonClick: (label: string) => ({
      category,
      action: 'SwitchLayout',
      actionType: 'click',
      label,
    }),
    graphicAccentButtonClick: (label: string) => ({
      category,
      action: 'TriedGraphicAccent',
      actionType: 'click',
      label,
    }),
    viewMatchingStationeryClick: () => ({
      category,
      action: 'ViewMatchingPrintsFromVendor',
      actionType: 'click',
      extraInfo: { source: 'ThemeOptionDialog' },
    }),
    onFontClick: (fontId: string, designId: string, fontPackCategory?: Category, isFontPack: boolean = false) => ({
      category,
      action: 'SelectFont',
      label: fontId,
      extraInfo: {
        designId,
        category: fontPackCategory,
        isFontPack,
      },
    }),
    filterClick: (title: string) => {
      let action = 'UnknownFilter';
      if (title === 'Art') {
        action = 'ThemeFilter';
      }
      return { category, action, actionType: 'click' };
    },
    onFontFilterCheckboxClick: (fontCategory: string, extraInfo: object = {}) => ({
      category,
      action: 'FilterByFontCategory',
      label: fontCategory,
      extraInfo: { ...extraInfo },
    }),
    customizeAndBuyClick: (themeName: string) => ({
      category,
      action: 'ViewMatchingPrintsFromVendor',
      label: themeName,
      extraInfo: { source: 'stationeryPage' },
    }),
    onLogoClick: (partner: PaperProviderEnum) => ({
      category,
      action: 'ExplorePrintPartner',
      label: partner,
    }),
    defaultArtBackgroundClick: () => ({
      category,
      action: 'UseArtDefaultBackground',
      actionType: 'click',
    }),
    triedStyleWithWeddingColorClick: () => ({
      category,
      action: 'TriedStyleWithWeddingColor',
    }),
    textContrastClick: () => ({
      category,
      action: 'TextContrast',
      actionType: 'click',
    }),
    colorPickerSquareClick: () => ({
      category,
      action: 'ColorPicker',
      actionType: 'click',
    }),
    hueBarClick: () => ({
      category,
      action: 'HueBar',
      actionType: 'click',
    }),
    hexClick: () => ({
      category,
      action: 'Hex',
      actionType: 'click',
    }),
    onViewSiteClick: () => ({
      category,
      action: 'OpenWebsite',
      label: 'View Site',
    }),
    onFabTap: (isPreviewVisible: boolean) => ({
      category,
      action: 'FabEdit',
      actionType: 'click',
      label: isPreviewVisible ? 'Edit Design' : 'Preview',
    }),
    filterBaseClick: (action: string = 'UnknownFilter', isOpen: boolean) => {
      return { category, action, actionType: 'click', label: isOpen ? 'Close' : 'Open' };
    },
    onFilterItemClick: (filterItem: { item: string; group: string; isChecked: boolean }) => {
      return { category, action: 'FilterItem', actionType: 'click', label: `${filterItem.group}: ${filterItem.item} (${filterItem.isChecked ? 'Check' : 'Uncheck'})` };
    },
    onGalleryViewCountClick: (viewCount: number) => {
      return { category, action: 'FilterItem', actionType: 'click', label: `${viewCount} in a row` };
    },
    onFilterItemClear: (group: string) => {
      return { category, action: 'Clear', actionType: 'click', label: group };
    },
    onFilterItemClearAll: (context: string) => {
      return { category, action: 'ClearAll', actionType: 'click', label: context };
    },
    onTemplateSelected: (template: ThemeVariant) => {
      return {
        category,
        action: 'TemplateOption',
        actionType: 'click',
        label: `${template.variantName || template.imageUrl} - ${template.variationType}`,
        extraInfo: {
          photoPresentationLayout: template.websiteDesignSettings?.photoPresentationLayout,
          websiteLayout: template.variationType,
          templateId: template.variantThemeId,
        },
      };
    },
    onContinueDesignClick: () => {
      return {
        category,
        action: 'DesignButtonInteracted',
        extraInfo: {
          name: 'continueEditingGalleryTile',
        },
      };
    },
    abTestAssignmentTemplatePicker: (assignment: boolean) => ({
      category,
      action: 'ABTestAssigned',
      extraInfo: {
        name: 'TemplatePicker',
        value: assignment ? 'Experiment' : 'Control',
      },
    }),
    stationeryTabV2ButtonInteracted: (args: { name: string; themeId: string }) => ({
      category,
      action: 'MessagingButtonInteracted',
      extraInfo: args,
    }),
    exploreMatchingPrintsButtonClick: (themeId: string) => ({
      category: 'admin.websiteDesignerV2',
      action: 'ButtonInteracted',
      extraInfo: {
        name: 'matchingPaperBanner_exploreNowCta',
        themeId,
      },
    }),
    // Search & Favorite telemetry
    toggleFavoriteClick: (designTab: WebsiteDesingerTabs, designId: string, designName: string, isFavorite: boolean, category?: Category) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'toggleFavoriteItem',
        designTab,
        designId,
        designName,
        isFavorite,
        category,
      },
    }),
    selectFavoriteClick: (designTab: WebsiteDesingerTabs, isActive: boolean, category?: Category) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'toggleViewFavorites',
        designTab,
        isActive,
        category,
      },
    }),
    onSearch: (designTab: WebsiteDesingerTabs, searchTerm: string, isActiveFavorite: boolean, isFontPack: boolean = false, category?: Category) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'onSearch',
        designTab,
        searchTerm,
        isActiveFavorite,
        isFontPack,
        category,
      },
    }),
    onClearCtaClick: (designTab: WebsiteDesingerTabs, cta: 'clearSearch' | 'clearFilters') => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'clearCta',
        designTab,
        cta,
      },
    }),
    onLetUsKnowCtaClick: (designTab: WebsiteDesingerTabs) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'letUsKnowCta',
        designTab,
      },
    }),
    printCrossSellDialogBrowseDesignsCtaClicked: () => ({
      category,
      action: 'ButtonInteracted',
      extraInfo: {
        name: 'printCrossSellDialog_browseDesignsCta',
      },
    }),
    printCrossSellDialogDismissClicked: () => ({
      category,
      action: 'ButtonInteracted',
      extraInfo: {
        name: 'printCrossSellDialog_dismiss',
      },
    }),
    onCustomizeFontCTAClick: (designId: string) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'customizeFontCta',
        designTab: WebsiteDesingerTabs.fonts,
        designId,
      },
    }),
    onFontSizeChange: (category: Category, fontSize: number, fontId: string, designId: string) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'fontSizeChanged',
        designTab: WebsiteDesingerTabs.fonts,
        category,
        label: fontId,
        fontSize,
        designId,
      },
    }),
    onResetFontCustomizationCTAClick: (designId: string) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'resetFontCustomizationCta',
        designTab: WebsiteDesingerTabs.fonts,
        designId,
      },
    }),
    onFontCategoryCTAClick: (category: Category, designId: string) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'fontCategoryCta',
        designTab: WebsiteDesingerTabs.fonts,
        category,
        designId,
      },
    }),
    onPreviewWithThisTemplate: (template: ThemeVariant) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'previewWithThisTemplate',
        designTab: WebsiteDesingerTabs.templates,
        themeId: template.theme?.themeId,
        layout: template.variationType,
        font: template.font,
        templateId: template.variantThemeId,
        photoPresentationLayout: template.websiteDesignSettings?.photoPresentationLayout,
      },
    }),
    onHeaderPhotoPresentationLayoutChange: (photoPresentationLayout: PhotoPresentationLayout, layout: DesignLayoutType) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'headerPhotoPresentationLayoutChange',
        layout,
        photoPresentationLayout,
      },
    }),
    onCustomizeThisDesignClick: (name: 'customizeThisDesign' | 'changeDesign', websiteLayout?: DesignLayoutType, presentationLayoutSchemaUrl?: string) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name,
        websiteLayout,
        presentationLayoutSchemaUrl,
      },
    }),
    onMonogramSaveError: (e: Error) => ({
      category,
      action: 'MonogramSave',
      actionType: 'error',
      extraInfo: { error: e.message },
    }),
    onViewWithPhotoCTAClick: (name: 'viewWithYourPhoto' | 'clearPhoto') => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name,
      },
    }),
    photoPaletteColorClicked: (color: string) => ({
      category: 'admin.websiteDesignerV2',
      action: 'DesignButtonInteracted',
      extraInfo: {
        name: 'photoPaletteColorClicked',
        color,
        designTab: DesignConsoleRouteName.Color,
      },
    }),
  },
  pages: pageTracking,
});

const { TelemetryProvider, useTelemetry } = createTelemetry(websiteDesignerTelemetry, { eventId: '' });
export { TelemetryProvider, useTelemetry as useWebsiteDesignerTelemetry };
